import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Play, X, Pause, Sparkles, Headphones, Clock, Wind, History } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import MentalWhyPanel from '../components/MentalWhyPanel';
import CheckinNudge from '../components/CheckinNudge';
import PillarTrendChart from '../components/PillarTrendChart';
import DistrictEntryButton from '../components/district/DistrictEntryButton';
import ScoreBreakdownPopover from '../components/ScoreBreakdownPopover';
import MeditationPlayer from '../components/MeditationPlayer';
import SoundShelf from '../components/mental/SoundShelf';
import api from '../services/api';

const getScoreColor = (s) => s >= 85 ? '#a3e635' : s >= 65 ? '#CCFF00' : s >= 45 ? '#facc15' : s >= 25 ? '#fb923c' : '#f87171';

const fmtSessionDate = (iso, localDate) => {
  const d = iso ? new Date(iso) : (localDate ? new Date(`${localDate}T00:00:00`) : null);
  if (!d || isNaN(d)) return '';
  const today = new Date(); const y = new Date(); y.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, y)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const PHASE_LABELS = ['INHALE', 'HOLD', 'EXHALE', 'HOLD'];
const PHASE_COLORS = ['#CCFF00', '#fbbf24', '#22d3ee', '#a855f7'];

/**
 * Mental page — Mental IQ score + a synthesized WHY (mood/stress/sleep drivers,
 * same shape as Recovery/Movement) + breathwork library. All scores read from
 * /api/wellness/iq → mental_insight, the same source Nikki uses.
 */

const BreathPlayer = ({ protocol, onClose }) => {
  const [phaseIdx, setPhaseIdx] = useState(0);   // 0=inhale, 1=hold, 2=exhale, 3=hold
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(true);
  const [secLeft, setSecLeft] = useState(protocol.pattern[0] || 4);
  const startRef = useRef(Date.now());
  const tickRef = useRef(null);

  const advance = useCallback(() => {
    setPhaseIdx(p => {
      let next = (p + 1) % 4;
      while (protocol.pattern[next] === 0 && next !== p) next = (next + 1) % 4;
      setSecLeft(protocol.pattern[next]);
      return next;
    });
  }, [protocol.pattern]);

  useEffect(() => {
    if (!running) return undefined;
    tickRef.current = setInterval(() => {
      setSecLeft(s => {
        if (s <= 1) { advance(); return protocol.pattern[(phaseIdx + 1) % 4] || 1; }
        return s - 1;
      });
      setElapsed(e => e + 1);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running, advance, protocol.pattern, phaseIdx]);

  const finish = async () => {
    const dur = Math.round((Date.now() - startRef.current) / 1000);
    try {
      await api.logBreathwork({ protocol: protocol.id, duration_seconds: dur });
      toast.success(`${protocol.name} done — ${Math.round(dur/60)} min logged.`);
      window.dispatchEvent(new Event('wellness:refresh'));
    } catch { /* logging is best-effort */ }
    onClose?.();
  };

  useEffect(() => {
    if (elapsed >= (protocol.default_seconds || 240)) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed]);

  const phaseColor = PHASE_COLORS[phaseIdx];
  const phaseLabel = PHASE_LABELS[phaseIdx];
  const remaining = (protocol.default_seconds || 240) - elapsed;
  const isInhale = phaseIdx === 0;
  const isExhale = phaseIdx === 2;
  const scale = isInhale ? 1.5 : isExhale ? 0.7 : 1.1;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col items-center justify-center safe-top safe-bottom" data-testid="breathwork-player">
      <button
        onClick={onClose}
        className="absolute z-20 p-3 -m-1 text-zinc-300 hover:text-white transition-colors"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          right: 'calc(env(safe-area-inset-right, 0px) + 8px)',
        }}
        data-testid="breathwork-close"
        aria-label="Close breathwork"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="text-center mb-2">
        <p className="text-xs text-zinc-500 uppercase tracking-widest">{protocol.purpose}</p>
        <h2 className="font-secondary font-bold text-2xl text-white mt-1">{protocol.name}</h2>
      </div>

      <div className="relative my-10" style={{ width: 280, height: 280 }}>
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, ${phaseColor}55 0%, ${phaseColor}00 70%)`,
            transform: `scale(${scale})`,
            transition: 'transform 4s ease-in-out',
          }}
        />
        <div
          className="absolute inset-8 rounded-full border-2 flex items-center justify-center"
          style={{
            borderColor: phaseColor,
            transform: `scale(${scale})`,
            transition: 'transform 4s ease-in-out, border-color 1s ease',
          }}
        >
          <div className="text-center">
            <p className="font-secondary font-bold text-4xl" style={{ color: phaseColor }}>{phaseLabel}</p>
            <p className="font-mono text-7xl mt-2" style={{ color: phaseColor }}>{secLeft}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={() => setRunning(r => !r)}
          className="w-14 h-14 rounded-full border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-800"
          data-testid="breathwork-toggle"
        >
          {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <div className="text-center">
          <p className="text-xs text-zinc-500">remaining</p>
          <p className="font-mono text-lg text-zinc-300">{Math.floor(remaining/60)}:{String(remaining%60).padStart(2,'0')}</p>
        </div>
        <button
          onClick={finish}
          className="px-4 py-2 bg-primary text-black text-xs font-bold uppercase tracking-wider rounded-sm"
          data-testid="breathwork-finish"
        >
          finish
        </button>
      </div>
    </div>
  );
};

const Mental = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [protocols, setProtocols] = useState([]);
  const [active, setActive] = useState(null);
  const [meditations, setMeditations] = useState([]);
  const [activeMeditation, setActiveMeditation] = useState(null);
  const [iq, setIq] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [layer, setLayer] = useState('sounds');  // which Mental layer is open

  const loadIq = useCallback(() => {
    api.get('/wellness/iq').then(r => setIq(r.data)).catch(() => {});
  }, []);
  const loadSessions = useCallback(() => {
    api.getMentalSessions(15).then(r => setSessions(r.data?.sessions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.getBreathworkProtocols().then(r => setProtocols(r.data?.protocols || [])).catch(() => {});
    api.meditationDefaults().then(r => setMeditations(r.data?.meditations || [])).catch(() => {});
    loadIq();
    loadSessions();
    const onRefresh = () => { loadIq(); loadSessions(); };
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, [loadIq, loadSessions]);

  const mentalIQ = iq?.sub_scores?.mental;
  const mentalInsight = iq?.mental_insight;
  const mentalBreakdown = iq?.score_breakdown?.mental;

  const body = (
    <>
      <div className={embedded ? 'max-w-2xl mx-auto px-5 py-8 space-y-6 living-skin' : 'max-w-6xl mx-auto px-4 py-8 space-y-6'} style={embedded ? { '--skin-rgb': '168,85,247' } : undefined}>
        {/* Header — Mental score + transparency tooltip */}
        {!embedded && (
        <div className="flex items-start justify-between gap-4">
          <PageHeader
            tag="Mental Readiness"
            tagColor="#a855f7"
            icon={Brain}
            title="Your readiness"
            subtitle="A few taps a day. Nikki uses what you log here to soften (or step up) tomorrow's plan."
            testIdPrefix="mental"
          />
          {mentalIQ != null && (
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary">Mental Readiness</p>
                <p className="font-secondary font-bold text-3xl" style={{ color: getScoreColor(mentalIQ) }} data-testid="mental-iq-score">{mentalIQ}</p>
              </div>
              <ScoreBreakdownPopover
                breakdown={mentalBreakdown}
                label="Mental Readiness formula"
                finalScore={mentalIQ}
                color="#a855f7"
                testId="mental-breakdown"
              />
            </div>
          )}
        </div>
        )}

        {/* Synthesized WHY — headline + action + mood/stress/sleep drivers.
            Hidden in the embedded world to avoid repeating Nikki's "why this
            world looks like this" read shown above. */}
        {!embedded && (
        <MentalWhyPanel
          insight={mentalInsight}
          score={mentalIQ}
          onActionClick={() => navigate('/chat')}
        />
        )}

        {/* Check-in lives on the Dashboard now — this is just a quick link. */}
        <CheckinNudge color="#a855f7" />

        <PillarTrendChart pillar="mental" color="#a855f7" label="Mental Readiness" />

        {!embedded && <DistrictEntryButton pillar="mental" accent="#A78BFA" name="Mental" />}

        {/* Three layers — tap a card to enter Sounds, Meditation or Breathwork */}
        <div className="grid grid-cols-3 gap-2" data-testid="mental-layers">
          {[
            { id: 'sounds', label: 'Sounds', icon: Headphones, color: '#C08CFF' },
            { id: 'meditation', label: 'Meditation', icon: Sparkles, color: '#5BDCF5' },
            { id: 'breathwork', label: 'Breathwork', icon: Wind, color: '#9BE33B' },
          ].map((l) => {
            const Icon = l.icon; const on = layer === l.id;
            return (
              <button key={l.id} onClick={() => setLayer(l.id)} data-testid={`mental-layer-${l.id}`}
                className="p-3 rounded-2xl border text-center transition-all duration-200 active:scale-95"
                style={{ borderColor: on ? l.color : 'rgba(255,255,255,0.1)', background: on ? `${l.color}1a` : 'rgba(0,0,0,0.3)', boxShadow: on ? `0 0 18px ${l.color}33` : 'none' }}>
                <Icon className="w-5 h-5 mx-auto" style={{ color: on ? l.color : '#a1a1aa' }} />
                <span className="block mt-1.5 text-xs font-secondary font-bold" style={{ color: on ? l.color : '#d4d4d8' }}>{l.label}</span>
              </button>
            );
          })}
        </div>

        {layer === 'sounds' && <SoundShelf pillar="mental" />}

        {layer === 'breathwork' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">BREATHWORK</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {protocols.map(p => (
              <button
                key={p.id}
                onClick={() => setActive(p)}
                className="hpme-card p-4 text-left hover:border-primary/40 transition-colors group"
                data-testid={`breathwork-card-${p.id}`}
              >
                <div className="hpme-card-corner" />
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-secondary font-bold text-lg">{p.name}</h3>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{Math.round(p.default_seconds/60)} min</span>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-primary mb-2">{p.purpose}</p>
                <p className="text-xs text-zinc-400 leading-relaxed">{p.description}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary group-hover:underline">
                  <Play className="w-3 h-3" /> start
                </div>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Guided voice meditations — Nikki-written, calm spoken sessions */}
        {layer === 'meditation' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Headphones className="w-4 h-4 text-cyan-300" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-secondary font-bold">MEDITATION</span>
            <span className="text-[10px] text-zinc-600">· press play & just listen</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {meditations.map(m => (
              <button
                key={m.id}
                onClick={() => setActiveMeditation({ meditationId: m.id, title: m.title, subtitle: m.subtitle, estMin: m.est_min })}
                className="hpme-card p-4 text-left hover:border-cyan-400/40 transition-colors group"
                data-testid={`meditation-default-${m.id}`}
              >
                <div className="hpme-card-corner" />
                <div className="w-9 h-9 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center mb-3">
                  <Headphones className="w-4 h-4 text-cyan-300" />
                </div>
                <h3 className="font-secondary font-bold text-base leading-tight">{m.title}</h3>
                <p className="text-xs text-zinc-400 mt-1">{m.subtitle}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="flex items-center gap-1 text-[11px] text-zinc-500">
                    <Clock className="w-3 h-3" /> ~{m.est_min} min
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-cyan-300 group-hover:underline">
                    <Play className="w-3 h-3" /> play
                  </span>
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/chat')}
            className="mt-3 text-xs text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="meditation-ask-nikki"
          >
            Want something specific? Ask Nikki for a custom meditation →
          </button>
        </div>
        )}

        {/* Mental Sessions — completed meditations + breathwork (like workout history) */}
        {sessions.length > 0 && (
          <div data-testid="mental-sessions-section">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-zinc-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-secondary font-bold">MENTAL SESSIONS</span>
              <span className="text-[10px] text-zinc-600">· what you've completed</span>
            </div>
            <div className="space-y-2">
              {sessions.map((s) => {
                const isMed = s.kind === 'meditation';
                const Icon = isMed ? Headphones : Wind;
                const mins = s.duration_seconds ? Math.max(1, Math.round(s.duration_seconds / 60)) : null;
                return (
                  <div
                    key={s.id}
                    className="hpme-card px-4 py-3 flex items-center gap-3"
                    data-testid={`mental-session-${s.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border shrink-0 ${isMed ? 'bg-cyan-500/15 border-cyan-400/30 text-cyan-300' : 'bg-primary/15 border-primary/30 text-primary'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-200 truncate">{s.name}</p>
                      <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{isMed ? 'Meditation' : 'Breathwork'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-zinc-300">{fmtSessionDate(s.completed_at, s.local_date)}</p>
                      {mins && <p className="text-[11px] text-zinc-500">{mins} min</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {active && <BreathPlayer protocol={active} onClose={() => { setActive(null); loadIq(); loadSessions(); }} />}
      {activeMeditation && (
        <MeditationPlayer
          meditationId={activeMeditation.meditationId}
          title={activeMeditation.title}
          subtitle={activeMeditation.subtitle}
          estMin={activeMeditation.estMin}
          onClose={() => { setActiveMeditation(null); loadSessions(); }}
        />
      )}
    </>
  );
  return embedded ? body : <Layout>{body}</Layout>;
};

export default Mental;
